import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { TabPanel, TabView } from 'primereact/tabview';
import { Toast } from 'primereact/toast';
import { useForm } from 'react-hook-form';

import '../css/AssetCard.css';
import { BaseFiasUrl } from 'constants/BaseUrl';
import { CustomDropdown } from 'ui/custom-controls';
import LocationTabContent from 'ui/detailedInformationCard/LocationTabContent';
import ConfirmationDialog from 'ui/shared/ConfirmationDialog';
import DeleteConfirmationDialog from 'ui/shared/DeleteConfirmationDialog';
import { withDialogFooterSpacing } from 'ui/shared/dialogFooterSpacing';

import { AssetsService } from '../../components/services/AssetsService';
import { GeographicalZoneService } from '../../components/services/GeographicalZoneService';
import { ApiResponse } from '../../components/services/interfaces';
import { ObjectForSelectionTemplate } from '../../constants/ObjectForSelectionTemplate';
import { AssetSchema } from '../../constants/schemas';
import { handleApiErrors } from '../../helpers';
import useAssetParametersService from '../../hooks/services/useAssetParametersService';
import useAssetTypesService from '../../hooks/services/useAssetTypesService';
import { useApiCall } from '../../hooks/useApiCall';
import {
  AssetParameter,
  DropdownSelectorType,
  FiasObject,
  ObjectForSelection
} from '../../types/interfaces';
import {
  AssetForEdit,
  AssetParameterValue
} from '../../types/interfaces/AssetForEdit';
import { AssetTriggerType } from '../../types/interfaces/triggers';
import { AssetProps } from '../../types/props';

import CharacteristicsTabContent from './CharacteristicsTabContent';
import MainTabContent from './MainTabContent';

interface RefInterface {
  handleCancel: () => void;
}

const defaultAsset: AssetForEdit = {
  id: 0,
  key: '',
  isFias: true,
  parameters: [],
  name: '',
  externalId: '',
  type: { key: '', label: '' },
  geographicalZone: null,
  address: '',
  cityFias: null,
  streetFias: null,
  houseFias: null,
  state: true,
  description: '',
  latitude: null,
  longitude: null,
  parameterValues: []
};

const AssetCard = React.forwardRef<RefInterface | null, AssetProps>(
  (
    {
      assetProps,
      onAdd,
      onUpdate,
      onDelete,
      closeDialog,
      isMobile,
      withoutDialog
    }: AssetProps,
    ref
  ) => {
    const {
      register,
      trigger,
      handleSubmit,
      watch,
      setError,
      setValue,
      reset,
      formState: { dirtyFields, errors }
    } = useForm<AssetForEdit>({
      resolver: zodResolver(AssetSchema),
      defaultValues: defaultAsset
    });

    const [selectedAssetType, setSelectedAssetType] =
      useState<DropdownSelectorType | null>(null);
    const [originalAssetType, setOriginalAssetType] =
      useState<DropdownSelectorType | null>(null);
    const [assetTypesFilterOptions, setAssetTypesFilterOptions] = useState<
      DropdownSelectorType[]
    >([]);
    // feature/AM-19479

    const [houses, setHouses] = useState<FiasObject[]>([]);
    const [loading, setLoading] = useState(false);
    // feature/AM-19479

    const state = watch('state');
    const assetId = watch('id');
    const name = watch('name');
    const type = watch('type');
    const parameters = watch('parameters');
    const parameterValues = watch('parameterValues');
    const cityFias = watch('cityFias');
    const isFias = watch('isFias');
    const longitude = watch('longitude');
    const geographicalZone = watch('geographicalZone');
    const [geographicalZonesOptions, setGeographicalZonesOptions] = useState<
      ObjectForSelection[]
    >([]);

    const assetParametersService = useAssetParametersService();
    const fetchAndUpdateCharacteristics = useCallback(
      async (
        type: string | undefined,
        parameterValuesArg: AssetParameterValue[] | undefined = undefined
      ) => {
        if (!parameterValues) parameterValuesArg = parameterValues;
        if (type !== undefined) {
          const assetTypesServiceResult =
            await assetParametersService.getByAssetType(parseInt(type))?.result;

          const parameters: AssetParameter[] = assetTypesServiceResult.map(
            (x): AssetParameter => ({ ...x, selectedValue: null })
          );
          setValue('parameters', parameters);
          if (parameterValuesArg) {
            parameters.forEach((x) => {
              const parameterValue = parameterValuesArg!.find(
                (y) => y.id === x.id
              );
              if (
                parameterValue &&
                parameterValue.value.key &&
                parameterValue.value.key !== ''
              )
                x.selectedValue = x.values.findIndex(
                  (y) =>
                    (y as any).id.toString() ===
                    parameterValue!.value.key.toString()
                );
            });
            setValue('parameters', parameters);
          } else setValue('parameterValues', []);
          return;
        }
        setValue('parameters', []);
      },
      [setValue]
    );

    const assetTypesService = useAssetTypesService();

    const getAssetApi = useCallback(async () => {
      const result = await assetTypesService.getForSelection()?.result;
      setAssetTypesFilterOptions(result ?? []);
      const geoZonesResp =
        await GeographicalZoneService.getGeographicalZonesForSelection();
      setGeographicalZonesOptions(
        geoZonesResp.data.isSuccess ? geoZonesResp.data.value ?? [] : []
      );

      // Функция для глубокого объединения объектов с дефолтными значениями
      const mergeWithDefaults = (
        asset: Partial<AssetForEdit>
      ): AssetForEdit => {
        return {
          ...defaultAsset,
          ...asset,
          parameters: asset.parameters || defaultAsset.parameters,
          parameterValues:
            asset.parameterValues || defaultAsset.parameterValues,
          cityFias: asset.cityFias || defaultAsset.cityFias,
          streetFias: asset.streetFias || defaultAsset.streetFias,
          houseFias: asset.houseFias || defaultAsset.houseFias,
          geographicalZone:
            asset.geographicalZone ?? defaultAsset.geographicalZone
        };
      };

      if (assetProps.id && assetProps.id !== 0) {
        try {
          const response = await AssetsService.getAssetById(
            parseInt(assetProps.id.toString())
          );

          if (response?.data) {
            // Объединяем данные с дефолтными значениями
            const mergedAsset = mergeWithDefaults(response.data);

            // Определяем режим ФИАС
            const isFiasMode =
              !!mergedAsset.cityFias ||
              !!mergedAsset.streetFias ||
              !!mergedAsset.houseFias;

            // Сброс формы с объединенными данными
            reset({
              ...mergedAsset,
              isFias: isFiasMode
            });

            // Загрузка домов если есть улица и город
            if (mergedAsset.streetFias && mergedAsset.cityFias) {
              fetchHouses(
                mergedAsset.streetFias.objectId,
                mergedAsset.cityFias.objectId
              );
            }

            // Обработка типа актива
            if (mergedAsset.type?.key) {
              const assetType = {
                id: mergedAsset.type.key,
                label: mergedAsset.type.label
              };

              setSelectedAssetType(assetType);
              setOriginalAssetType(assetType);

              await fetchAndUpdateCharacteristics(
                mergedAsset.type.key.toString(),
                mergedAsset.parameterValues
              );
            }
          }
        } catch (error) {
          console.error('Ошибка загрузки актива:', error);
          // Сброс к дефолтным значениям при ошибке
          reset(defaultAsset);
        }
      } else {
        // Сброс к полным дефолтным значениям для нового актива
        reset(defaultAsset);
      }
    }, [
      fetchAndUpdateCharacteristics,
      assetProps.id,
      reset,
      assetTypesService
    ]);

    useApiCall(getAssetApi);

    const [showDialog, setShowDialog] = useState(false);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);

    const [, forceUpdate] = useState<number>(0);

    const [isWarningDialogVisible, setWarningDialogVisible] = useState(false);
    const [isResetDialogVisible, setResetDialogVisible] = useState(false);

    const toast = useRef<Toast>(null);

    useEffect(() => {
      setOriginalAssetType(selectedAssetType);
    }, [selectedAssetType]);

    const resetDropdownValues = () => {
      if (type) {
        setResetDialogVisible(true);
        forceUpdate((prev) => prev + 1);
      }
    };

    const handleResetConfirmation = async () => {
      setResetDialogVisible(false);

      // Reset values
      setSelectedAssetType(null);
      setValue(
        'type',
        { key: '', label: '' },
        { shouldDirty: true, shouldValidate: true }
      );
      trigger('type');
      await fetchAndUpdateCharacteristics(undefined);
      // Trigger the input change event for 'type'
    };

    const handleResetCancellation = () => {
      setResetDialogVisible(false);
    };

    const handleAssetTypeChange = async (
      newAssetType: DropdownSelectorType | null
    ) => {
      if (
        originalAssetType &&
        newAssetType &&
        originalAssetType.label &&
        originalAssetType.label !== newAssetType.label
      ) {
        // Show the warning dialog if there are unsaved changes
        setWarningDialogVisible(true);
        setOriginalAssetType(newAssetType);
      } else if (!newAssetType?.label) {
        resetDropdownValues();
      } else {
        setValue(
          'type',
          {
            key: newAssetType.id?.toString() ?? '',
            label: newAssetType.label ?? ''
          },
          { shouldDirty: true, shouldValidate: true }
        );
        trigger('type');
        setOriginalAssetType(newAssetType);
        await fetchAndUpdateCharacteristics(newAssetType.id?.toString());
      }
    };

    const handleWarningContinue = async () => {
      setWarningDialogVisible(false);
      setSelectedAssetType(originalAssetType); // Revert back to the original value
      setValue(
        'type',
        {
          key: originalAssetType?.id?.toString() ?? '',
          label: originalAssetType?.label ?? ''
        },
        { shouldDirty: true, shouldValidate: true }
      );
      trigger('type');
      await fetchAndUpdateCharacteristics(originalAssetType?.id?.toString());
    };

    const handleWarningCancel = () => {
      setWarningDialogVisible(false);
    };

    const сharacteristicsErrors = useMemo(() => {
      if (errors.parameters) {
        return true;
      } else {
        return false;
      }
    }, [errors.parameters]);

    const locationErrors = useMemo(() => {
      if (
        errors.address ||
        errors.cityFias ||
        errors.houseFias ||
        errors.latitude ||
        errors.streetFias
      ) {
        return true;
      } else {
        return false;
      }
    }, [
      errors.address,
      errors.cityFias,
      errors.houseFias,
      errors.latitude,
      errors.streetFias
    ]);

    const mainErrors = useMemo(() => {
      if (errors.state || errors.name || errors.type || errors.externalId || errors.description) {
        return true;
      } else {
        return false;
      }
    }, [errors.state, errors.name, errors.type, errors.externalId, errors.description]);

    useEffect(() => {
      setSelectedAssetType(!type ? ObjectForSelectionTemplate : type);
    }, [type]);

    const onSubmit = async (data: AssetForEdit) => {
      const newData = { ...data, parameters };
      let result: ApiResponse<AssetForEdit> | undefined;
      if (newData.id !== null && newData.id !== undefined && newData.id !== 0) {
        result = await onUpdate?.(newData.id, newData);
      } else {
        newData.id = 0;
        result = await onAdd?.(newData);
      }
      if (result?.isSuccess) {
        closeDialog?.();
      } else {
        handleApiErrors({
          error: result?.error,
          setError,
          fieldMapping: {
            SameName: {
              field: 'externalId',
              message:
                'Запись с таким названием уже есть в справочнике. Пожалуйста, укажите уникальный id'
            },
            WrongFormat: {
              field: 'externalId',
              message: 'Ошибка формата внешнего №'
            }
          },
          customHandlers: {
            'Необходимо указать и широту, и долготу': () => {
              longitude
                ? setError('latitude', {
                  type: 'value',
                  message: result?.error
                })
                : setError('longitude', {
                  type: 'value',
                  message: result?.error
                });
            }
          }
        });
      }
    };

    const handleInputChange = (currentTrigger: AssetTriggerType) => {
      trigger(currentTrigger);
    };

    const handleCancel = async () => {
      if (Object.keys(dirtyFields).length > 0) {
        setShowDialog(true);
      } else {
        if (closeDialog) {
          closeDialog();
        }
      }
    };

    useImperativeHandle(ref, () => {
      return {
        handleCancel
      };
    });

    const handleDelete = () => {
      setShowDeleteDialog(true);
    };

    const handleDeleteConfirmation = () => {
      if (onDelete) {
        onDelete(assetId);
        closeDialog && closeDialog();
      }
      setShowDeleteDialog(false);
    };

    const handleDeleteCancellation = () => {
      setShowDeleteDialog(false);
    };

    const handleConfirmationContinue = () => {
      setShowDialog(false);
      if (closeDialog) {
        closeDialog();
      }
    };

    const handleConfirmationCancel = () => {
      setShowDialog(false);
    };

    const onError = useCallback((errors: any) => {
      if (toast.current && errors) {
        toast.current?.show({
          severity: 'error',
          summary: 'Ошибка сохранения',
          detail: 'Проверьте корректность заполнения полей',
          life: 3000
        });
      }
    }, []);

    // feature/AM-19479
    const fetchHouses = async (streetId: number, passedCityId?: number) => {
      setLoading(true);
      const response = await fetch(
        `${BaseFiasUrl}/city/${
          passedCityId ?? cityFias!.objectId
        }/street/${streetId}/house`
      );
      const data = await response.json();
      setHouses(data);
      setLoading(false);
    };
    // feature/AM-19479

    return (
      <div
        className={`${
          !withoutDialog && 'asset-card__roles'
        } asset-card  flex flex-column`}
        style={{ height: '100%' }}
      >
        <Toast ref={toast} />
        {!withoutDialog ? (
          <>
            <TabView>
              <TabPanel
                header="Основная"
                className={mainErrors ? 'error-tab' : ''}
              >
                <MainTabContent
                  state={state}
                  register={register}
                  setValue={setValue}
                  watch={watch}
                  errors={errors}
                  handleInputChange={handleInputChange}
                  selectedAssetType={selectedAssetType}
                  assetTypesFilterOptions={assetTypesFilterOptions}
                  handleAssetTypeChange={handleAssetTypeChange}
                  isMobile={isMobile}
                />
              </TabPanel>
              <TabPanel
                className={locationErrors ? 'error-tab' : ''}
                header="Расположение"
              >
                <LocationTabContent
                  isMobile={isMobile}
                  isFias={isFias!}
                  setValue={setValue}
                  register={register}
                  watch={watch}
                  errors={errors}
                  houses={houses}
                  fetchHouses={fetchHouses}
                  inputsClassNames="detailed-card__input"
                  errorsClassNames="detailed-card-errors"
                  extraContent={
                    <div className="flex flex-column gap-2">
                      <div className="flex flex-row justify-content-between align-items-center gap-3">
                        <label style={{ minWidth: '103.69px' }}>
                          Географическая<br/>зона
                        </label>
                        <div className="col p-0">
                          <CustomDropdown<ObjectForSelection>
                            passedClassName="w-full"
                            assetOptions={geographicalZonesOptions}
                            selectedAsset={geographicalZone ?? null}
                            onSelectionChange={(selected) => {
                              setValue('geographicalZone', selected as any, {
                                shouldDirty: true,
                                shouldValidate: true
                              });
                            }}
                            placeholder="Выберите географическую зону"
                            emptyMessage="Географические зоны не найдены."
                            minWidth={320}
                            showDeletedWarning
                            deletedWarningTooltip="Географическая зона удалена в справочнике"
                            showClearOnHover
                          />
                        </div>
                      </div>
                    </div>
                  }
                />
              </TabPanel>
              <TabPanel
                key={JSON.stringify(parameters)}
                className={сharacteristicsErrors ? 'error-tab' : ''}
                header="Характеристики"
              >
                {type ? (
                  <CharacteristicsTabContent
                    assetId={assetId}
                    setValue={setValue}
                    characteristics={parameters}
                    withoutDialog={withoutDialog ?? false}
                    isMobile={isMobile}
                  />
                ) : (
                  <div>Выберите тип объекта для отображения характеристик</div>
                )}
              </TabPanel>
            </TabView>

            <div className={`${isMobile ? 'new-card__buttons--mobile' : ''} new-card__buttons`}>
              <div 
                className="flex gap-3" 
                style={{
                  flexDirection: isMobile ? 'column' : 'row',
                }}
              >
                <Button
                  label="Сохранить"
                  icon={isMobile ? undefined : 'pi pi-check'}
                  onClick={handleSubmit(onSubmit, onError)}
                />
                <Button
                  label="Удалить"
                  icon={isMobile ? undefined : 'pi pi-trash'}
                  onClick={handleDelete}
                  className="p-button-danger"
                />
                <Button
                  label="Отмена"
                  icon={isMobile ? undefined : 'pi pi-times'}
                  onClick={handleCancel}
                  className="p-button-secondary"
                />
              </div>
            </div>

            <Dialog
              visible={isResetDialogVisible}
              onHide={handleResetCancellation}
              header="Подтверждение сброса"
              pt={withDialogFooterSpacing()}
              footer={
                <div>
                  <Button
                    className="p-button-danger"
                    label="Продолжить"
                    onClick={handleResetConfirmation}
                  />
                  <Button
                    label="Отмена"
                    onClick={handleResetCancellation}
                    className="p-button-secondary"
                  />
                </div>
              }
            >
              <div>
                Вы уверены, что хотите сбросить значения типа объекта?
                <br />
                Произойдет сброс характеристик и их значений.
              </div>
            </Dialog>

            <Dialog
              visible={isWarningDialogVisible}
              onHide={handleWarningCancel}
              header="Предупреждение"
              pt={withDialogFooterSpacing()}
              footer={
                <div>
                  <Button
                    className="p-button-danger"
                    label="Продолжить"
                    onClick={handleWarningContinue}
                  />
                  <Button
                    label="Отмена"
                    onClick={handleWarningCancel}
                    className="p-button-secondary"
                  />
                </div>
              }
            >
              <div>
                При изменении типа объекта произойдет изменение набора
                характеристик и сброс их значений. Вы хотите продолжить?
              </div>
            </Dialog>

            <ConfirmationDialog
              visible={showDialog}
              onCancel={handleConfirmationCancel}
              onContinue={handleConfirmationContinue}
            />
 

            <DeleteConfirmationDialog
              visible={showDeleteDialog}
              onHide={handleDeleteCancellation}
              onConfirm={handleDeleteConfirmation}
              entityType="объект"
              entityName={name}
            />
          </>
        ) : (
          <>
            <MainTabContent
              state={state}
              register={register}
              setValue={setValue}
              watch={watch}
              errors={errors}
              handleInputChange={handleInputChange}
              selectedAssetType={selectedAssetType}
              assetTypesFilterOptions={assetTypesFilterOptions}
              handleAssetTypeChange={handleAssetTypeChange}
              withoutDialog
            />
            <CharacteristicsTabContent
              assetId={assetId}
              setValue={setValue}
              characteristics={parameters}
              withoutDialog={withoutDialog}
            />
          </>
        )}
      </div>
    );
  }
);

AssetCard.displayName = 'AssetCard';

export default AssetCard;
